import { SURVEY_PAGES, PAGE_ACTIONS, effectiveSurveyAccess } from '../components/AdminList/permissions-config'

const usesExplicitPermissions = permissions => Number(permissions?.version || 0) >= 2

export function hasPageAccess(admin, page) {
  if (!admin) return false
  if (admin.role === 'super_admin' || admin.role === 'admin') return true
  const pages = admin.permissions?.pages
  return pages === 'all' || (Array.isArray(pages) && pages.includes(page))
}

// New grants use immutable catalog ids; old title grants remain readable.
export function matchesGuideline(allowed, value) {
  const g = value && typeof value === 'object' ? (value.guideline || value) : null
  const keys = g ? [g.id != null ? `id:${g.id}` : null, g.uid ? `uid:${g.uid}` : null, g.short_title, g.title, g.name] : [value]
  return keys.some(key => typeof key === 'string' && key.length > 0 && allowed.includes(key))
}

function matchesPageGuideline(page, allowed, value) {
  if (page !== 'leitlinien-umfragen') return matchesGuideline(allowed, value)
  const id = value && typeof value === 'object' ? value.guideline_id : value
  const key = id == null || id === '' ? 'standalone' : String(id).startsWith('hub:') ? String(id) : 'hub:' + String(id)
  return allowed.includes(key)
}

// Check if admin has access to a specific guideline on a survey page
export function hasSurveyAccess(admin, page, guidelineId) {
  if (!admin) return false
  if (admin.role === 'super_admin' || admin.role === 'admin') return true

  const permissions = admin.permissions
  if (!permissions) return false

  // Check page access first
  const pages = permissions.pages
  if (pages !== 'all' && !(Array.isArray(pages) && pages.includes(page))) return false

  // If not a survey page, page access is enough
  if (!SURVEY_PAGES.includes(page)) return true

  // Check survey_access for guideline restriction
  const surveyAccess = effectiveSurveyAccess(permissions, page)
  if (!surveyAccess) {
    if (usesExplicitPermissions(permissions)) return false
    // Backward compat: no survey_access = check old guidelines field or default to all
    const guidelines = permissions.guidelines
    if (guidelines === 'all') return true
    if (!guidelines) return !usesExplicitPermissions(permissions)
    if (Array.isArray(guidelines)) return usesExplicitPermissions(permissions)
      ? matchesGuideline(guidelines, guidelineId)
      : !guidelineId || matchesGuideline(guidelines, guidelineId)
    return !usesExplicitPermissions(permissions)
  }

  // Individual native survey grants take precedence over any old guideline field.
  if (page === 'leitlinien-umfragen' && Object.prototype.hasOwnProperty.call(surveyAccess, 'survey_ids')) {
    const row = guidelineId && typeof guidelineId === 'object' ? guidelineId : null
    if (!row?.id) return false
    const ids = surveyAccess.survey_ids
    if (ids === 'all' || (Array.isArray(ids) && ids.some(id => String(id) === String(row.id)))) return true
    return surveyAccess.actions?.create === true && !!admin.created_on && row.created_by === admin.created_on
  }
  if (page === 'fragenvorlagen') return true
  const guidelines = surveyAccess.guidelines
  if (guidelines === 'all') return true
  if (!guidelines) return !usesExplicitPermissions(permissions)
  if (Array.isArray(guidelines)) return usesExplicitPermissions(permissions)
    ? matchesPageGuideline(page, guidelines, guidelineId)
    : !guidelineId || matchesPageGuideline(page, guidelines, guidelineId)
  return !usesExplicitPermissions(permissions)
}

// Check if admin can write (create/edit/delete) on a survey page.
// Kept for backward compatibility: returns true if ANY of the granular
// actions is allowed. New code should call canPerformAction(...) instead.
export function canWrite(admin, page) {
  return ['create', 'edit', 'invite', 'delete'].some(action => canPerformAction(admin, page, action))
}

// Granular per-action permission check for survey pages.
//   action ∈ { 'create' | 'edit' | 'invite' }
// Defaults to TRUE when no config is present (backward compat) so adding
// this check to an existing route does not silently lock out admins who
// haven't been re-saved through the new form yet.
export function canPerformAction(admin, page, action) {
  if (action === 'export') action = 'results'
  if (!admin) return false
  if (admin.role === 'super_admin' || admin.role === 'admin') return true

  const permissions = admin.permissions
  if (!permissions) return false

  // Page-level access is required first.
  const pages = permissions.pages
  if (pages !== 'all' && !(Array.isArray(pages) && pages.includes(page))) return false

  // Non-survey pages have no per-action granularity yet.
  if (!SURVEY_PAGES.includes(page)) return true
  if (!PAGE_ACTIONS[page].includes(action) && !(page === 'abfragen' && action === 'create' && Number(permissions.version || 0) < 3)) return false

  const sa = effectiveSurveyAccess(permissions, page)
  if (!sa) return !usesExplicitPermissions(permissions)

  if (sa.actions && typeof sa.actions === 'object') {
    return usesExplicitPermissions(permissions) ? sa.actions[action] === true : sa.actions[action] !== false
  }
  // Legacy access_level fallback: 'read' blocks every write action.
  if (sa.access_level === 'read') return false
  return !usesExplicitPermissions(permissions)
}


export function managerHome(user) {
  if (['admin', 'super_admin'].includes(user?.role)) return '/activity'
  const routes = ['abstimmungen', 'konsultation', 'abfragen', 'leitlinien', 'stageleitlinien', 'livearbeitsversionen', 'authors', 'fragenvorlagen', 'app-analytics', 'hub-analytics', 'activity', 'feedback', 'leitlinien-umfragen', 'leitlinien-kommentare', 'app-benachrichtigungen', 'hub-leitlinien', 'newsletter', 'emailvorlagen', 'deepsearch', 'abstimmungen-analytics']
  const page = routes.find(page => hasPageAccess(user, page))
  return page ? '/' + page : '/profile'
}
